import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, TrendingDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlatformStageBadge } from "@/components/crm/status-badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	formatMrr,
	formatTimeAgo,
	SALES_STAGES,
	type SalesListRow,
	type SalesStage,
} from "@/lib/sales-format";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/sales/")({
	component: SalesListPage,
});

const PAGE_SIZE = 50;

type SortKey = "mrr" | "lastActivity" | "createdAt";

function SalesListPage() {
	const { t, i18n } = useTranslation();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [stage, setStage] = useState<SalesStage | null>(null);
	const [sort, setSort] = useState<SortKey>("createdAt");
	const [page, setPage] = useState(0);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
			setPage(0);
		}, 300);
		return () => clearTimeout(timer);
	}, [search]);

	const listInput = {
		search: debouncedSearch || undefined,
		stage: stage ?? undefined,
		sort,
		limit: PAGE_SIZE,
		offset: page * PAGE_SIZE,
	};

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "sales", "list", listInput],
		queryFn: async () => client.admin.sales.listOrganizations(listInput),
		staleTime: 60 * 1000,
	});

	const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
	const dateFormat = new Intl.DateTimeFormat(i18n.language, {
		dateStyle: "medium",
	});

	return (
		<div className="space-y-4">
			<div>
				<h1 className="font-bold text-3xl">{t("admin:sales.title")}</h1>
				<p className="text-muted-foreground">{t("admin:sales.subtitle")}</p>
			</div>

			{/* Funnel chips: counts respect the search, clicking filters by stage. */}
			<div className="flex flex-wrap gap-2">
				{SALES_STAGES.map((s) => (
					<Button
						key={s}
						variant={stage === s ? "default" : "outline"}
						size="sm"
						onClick={() => {
							setStage(stage === s ? null : s);
							setPage(0);
						}}
					>
						{t(`admin:sales.stage.${s}`)}
						<Badge variant="secondary" className="ml-2">
							{data?.funnelCounts[s] ?? 0}
						</Badge>
					</Button>
				))}
			</div>

			<Input
				placeholder={t("admin:sales.search_placeholder")}
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				className="max-w-sm"
			/>

			{isLoading ? (
				<div className="space-y-2">
					<Skeleton className="h-10" />
					<Skeleton className="h-10" />
					<Skeleton className="h-10" />
				</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{t("admin:sales.column.organization")}</TableHead>
							<TableHead>{t("admin:sales.column.stage")}</TableHead>
							<TableHead>
								<button
									type="button"
									className="font-medium hover:underline"
									onClick={() => setSort("mrr")}
								>
									{t("admin:sales.column.mrr")}
									{sort === "mrr" ? " ↓" : ""}
								</button>
							</TableHead>
							<TableHead>{t("admin:sales.column.members")}</TableHead>
							<TableHead>
								<button
									type="button"
									className="font-medium hover:underline"
									onClick={() => setSort("lastActivity")}
								>
									{t("admin:sales.column.last_activity")}
									{sort === "lastActivity" ? " ↓" : ""}
								</button>
							</TableHead>
							<TableHead>
								<button
									type="button"
									className="font-medium hover:underline"
									onClick={() => setSort("createdAt")}
								>
									{t("admin:sales.column.created")}
									{sort === "createdAt" ? " ↓" : ""}
								</button>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{(data?.organizations ?? []).map((org: SalesListRow) => (
							<TableRow key={org.id}>
								<TableCell>
									<Link
										to="/sales/$organizationId"
										params={{ organizationId: org.id }}
										className="font-medium hover:underline"
									>
										{org.name}
									</Link>
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-1">
										<PlatformStageBadge stage={org.stage} />
										{org.churnRisk && (
											<Badge
												variant="destructive"
												title={t("admin:sales.churn_risk")}
											>
												<TrendingDown className="h-3 w-3" />
											</Badge>
										)}
									</div>
								</TableCell>
								<TableCell>
									<span className="flex items-center gap-1">
										{formatMrr(org.mrrCents, org.currencyCode, i18n.language)}
										{org.mixedCurrency && (
											<AlertTriangle
												className="h-4 w-4 text-amber-500"
												aria-label={t("admin:sales.mixed_currency")}
											/>
										)}
									</span>
								</TableCell>
								<TableCell>{org.memberCount}</TableCell>
								<TableCell>
									{org.lastActivityAt ? (
										<span
											title={dateFormat.format(new Date(org.lastActivityAt))}
										>
											{formatTimeAgo(
												new Date(org.lastActivityAt),
												i18n.language,
											)}
										</span>
									) : (
										"—"
									)}
								</TableCell>
								<TableCell>
									{dateFormat.format(new Date(org.createdAt))}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<div className="flex items-center justify-between">
				<span className="text-muted-foreground text-sm">
					{t("admin:sales.total_orgs", { count: data?.total ?? 0 })}
				</span>
				<div className="flex gap-2">
					<Button
						variant="outline"
						size="sm"
						disabled={page === 0}
						onClick={() => setPage((p) => p - 1)}
					>
						{t("common:previous")}
					</Button>
					<span className="self-center text-sm">
						{page + 1} / {totalPages}
					</span>
					<Button
						variant="outline"
						size="sm"
						disabled={page + 1 >= totalPages}
						onClick={() => setPage((p) => p + 1)}
					>
						{t("common:next")}
					</Button>
				</div>
			</div>
		</div>
	);
}
